import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, Table } from 'react-bootstrap';

import logo from '../../assets/images/logo-new.png';
import MainTemplate from '../../shared/templates/MainTemplate/MainTemplateContainer';
import { BASE_URL, FRONTEND_URL, WS_URL } from '../../config';
import './LiveResults.css';

const POLL_INTERVAL = 5000;
const MAX_RECONNECT_DELAY = 30000;
const OPTION_KEYS = Array.from({ length: 10 }, (_, index) => `option_${index + 1}`);

// live_survey_stats and the WebSocket send the same question shape; older backends put
// the title only at the top level.
const questionTitle = (question, index) => (question.questions && question.questions.question_title)
    || question.question_title
    || `Question ${index + 1}`;

const voteCount = (question, key) => parseInt((question.votes && question.votes[key]) || 0, 10);

const questionTotal = (question) => OPTION_KEYS.reduce((sum, key) => sum + voteCount(question, key), 0);

const getPercentage = (votes, total) => (total > 0 ? Math.round((votes / total) * 100) : 0);

const LiveResults = ({ match }) => {
    const surveyCode = new URLSearchParams(window.location.search).get('survey_code') || match.params.surveyCode;
    const [surveyData, setSurveyData] = useState(null);
    const [questionsData, setQuestionsData] = useState([]);
    const [error, setError] = useState(surveyCode ? null : 'No survey code provided');
    const [pageLoading, setPageLoading] = useState(Boolean(surveyCode));
    const [connected, setConnected] = useState(false);

    const surveyId = surveyData ? surveyData.survey_id : null;

    // Results over HTTP: the first load, and polling while the WebSocket is down.
    const fetchResults = useCallback(() => fetch(`${BASE_URL}/live_survey_stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey_code: surveyCode }),
    })
        .then((response) => response.json())
        .then((data) => {
            if (data.error) {
                setError(data.message);
            } else {
                setError(null);
                setSurveyData(data.surveyData);
                setQuestionsData(data.questionsData || []);
            }
        }), [surveyCode]);

    useEffect(() => {
        if (!surveyCode) {
            return;
        }
        fetchResults()
            .catch(() => setError('Failed to fetch survey results'))
            .then(() => setPageLoading(false));
    }, [surveyCode, fetchResults]);

    // Push updates from the backend's WebSocket server: join the survey's room and get
    // fresh counts after every vote. Reconnects with backoff.
    useEffect(() => {
        if (!surveyId) {
            return undefined;
        }
        let socket = null;
        let reconnectTimer = null;
        let reconnectDelay = 1000;
        let stopped = false;

        const connect = () => {
            socket = new WebSocket(WS_URL);
            socket.onopen = () => {
                reconnectDelay = 1000;
                setConnected(true);
                socket.send(JSON.stringify({ type: 'join_survey', survey_id: surveyId }));
            };
            socket.onmessage = (event) => {
                let message;
                try {
                    message = JSON.parse(event.data);
                } catch (e) {
                    return;
                }
                if (message.type === 'live_stats' && String(message.survey_id) === String(surveyId)
                    && message.data && message.data.questions_data) {
                    setQuestionsData(message.data.questions_data);
                } else if (message.type === 'error') {
                    // e.g. live mode was switched off. Stop; polling reports the API's answer.
                    stopped = true;
                    socket.close();
                }
            };
            socket.onclose = () => {
                setConnected(false);
                if (!stopped) {
                    reconnectTimer = setTimeout(connect, reconnectDelay);
                    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
                }
            };
        };
        connect();

        return () => {
            stopped = true;
            clearTimeout(reconnectTimer);
            if (socket) {
                socket.close();
            }
        };
    }, [surveyId]);

    useEffect(() => {
        if (!surveyId || connected) {
            return undefined;
        }
        // Keep showing the last results if a poll fails.
        const timer = setInterval(() => fetchResults().catch(() => {}), POLL_INTERVAL);
        return () => clearInterval(timer);
    }, [surveyId, connected, fetchResults]);

    if (pageLoading) {
        return (
            <div className="live-results-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading survey results...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="live-results-container">
                <div className="error-message">
                    <h2>❌ Error</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!surveyData) {
        return (
            <div className="live-results-container">
                <div className="error-message">
                    <h2>Survey Not Found</h2>
                    <p>The survey with code "{surveyCode}" could not be found.</p>
                </div>
            </div>
        );
    }

    const kpiGoal = Number(surveyData.kpi_goal) || 0;
    // Each respondent answers every question once, so the busiest question counts respondents.
    const responses = questionsData.reduce((max, question) => Math.max(max, questionTotal(question)), 0);

    return (
        <MainTemplate>
            <section className="section__thanks live-results-page">
                <Container className="d-flex justify-content-center text-center">
                    <Card className="card__container">
                        <Card.Body>
                            <Link to="/"><img className="cursor-pointer" src={logo} alt="logo" /></Link>

                            <div className="pb-3 h-100 d-flex justify-content-center flex-column">

                                {/* Live Status Indicator */}
                                <div className="live-indicator mb-3">
                                    <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`}></span>
                                    <span className="status-text">
                                        {connected ? '🔴 LIVE RESULTS' : '⚫ OFFLINE'}
                                    </span>
                                </div>

                                {/* Survey Title */}
                                <h4 className="mb-3">{surveyData.survey_title}</h4>

                                {/* Survey Info */}
                                <p className="mb-4">
                                    <strong>Survey Code:</strong> {surveyData.survey_code}
                                </p>

                                {/* Results Table */}
                                <div className="results-table-container">
                                    {questionsData.map((questionData, qIndex) => {
                                        const optionKeys = OPTION_KEYS.filter((key) => questionData.options && questionData.options[key]);
                                        if (!optionKeys.length) {
                                            // Free-text questions have nothing to count.
                                            return null;
                                        }
                                        const total = questionTotal(questionData);

                                        return (
                                            <div key={qIndex} className="question-results mb-5">
                                                <h5 className="question-title-table mb-3">
                                                    {questionTitle(questionData, qIndex)}
                                                </h5>

                                                <Table striped bordered hover className="results-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Option</th>
                                                            <th>Votes</th>
                                                            <th>Percentage</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {optionKeys.map((optionKey, oIndex) => {
                                                            const votes = voteCount(questionData, optionKey);
                                                            const percentage = getPercentage(votes, total);

                                                            return (
                                                                <tr key={optionKey} className="result-row">
                                                                    <td className="option-text-table">{questionData.options[optionKey]}</td>
                                                                    <td className="votes-cell">
                                                                        <span className="vote-count">{votes}</span>
                                                                    </td>
                                                                    <td className="percentage-cell">
                                                                        <div className="percentage-container">
                                                                            <div
                                                                                className="percentage-bar"
                                                                                style={{
                                                                                    width: `${percentage}%`,
                                                                                    backgroundColor: `hsl(${(oIndex * 60) % 360}, 70%, 50%)`
                                                                                }}
                                                                            ></div>
                                                                            <span className="percentage-text">{percentage}%</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Footer Message */}
                                <Card.Text className="live-footer-text">
                                    Results update in real-time as new votes come in{' '}
                                    <a href={`${FRONTEND_URL}/`} rel="noopener noreferrer" target="_blank">
                                        SurveyStud.com
                                    </a>
                                    {kpiGoal > 0 && responses >= kpiGoal && (
                                        <div className="goal-achieved-text mt-2">
                                            🎉 Survey goal achieved! ({responses}/{kpiGoal})
                                        </div>
                                    )}
                                </Card.Text>

                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            </section>
        </MainTemplate>
    );
};

export default LiveResults;
